import { Link } from "@tanstack/react-router"
import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import type { ProductVisualStudioCard, StoreCode } from "@nova-ai/shared"
import { ArrowUpRight } from "lucide-react"

import { humanizeEnumLabel } from "../../lib/enum-label"
import { formatDateTime } from "../../lib/format-date"

export function ProductVisualReviewDetails({
  image,
  store,
  visualKind,
}: {
  image: ProductVisualStudioCard
  store: StoreCode
  visualKind: ProductVisualKind
}) {
  const artifact = image.cell.artifact
  return (
    <aside className="min-h-0 overflow-y-auto border-t border-border bg-card px-5 py-5 md:border-l md:border-t-0">
      <dl className="space-y-3 text-sm">
        <MetadataRow label="Simple SKU" value={image.simpleSku} isCode />

        <MetadataRow label="Freshness" value={humanizeEnumLabel(image.cell.freshness)} />

        {artifact ? (
          <MetadataRow label="Generated" value={formatDateTime(artifact.createdAt)} />
        ) : null}

        {artifact ? <MetadataRow label="Source" value={provenanceLabel(artifact.source)} /> : null}

        {artifact ? (
          <MetadataRow label="Attribution" value={provenanceLabel(artifact.attributionMode)} />
        ) : null}

        {artifact ? <MetadataRow label="AI used" value={aiUsedLabel(artifact.aiUsed)} /> : null}

        {artifact ? (
          <MetadataRow
            label="Template"
            value={`v${artifact.templateVersion} · current v${artifact.currentTemplateVersion}`}
          />
        ) : null}

        {artifact ? (
          <MetadataRow label="Dimensions" value={`${artifact.width} × ${artifact.height} px`} />
        ) : null}

        {image.cell.replacement ? (
          <MetadataRow
            label="Detected"
            value={`${formatDateTime(image.cell.replacement.detectedAt)} · ${humanizeEnumLabel(image.cell.replacement.trigger)}`}
          />
        ) : null}
      </dl>

      {image.cell.replacementHistory?.length ? (
        <section className="mt-5 border-t border-border pt-4">
          <h3 className="text-xs font-medium text-muted-foreground">Replacement history</h3>

          <ol className="mt-3 space-y-3 text-xs">
            {image.cell.replacementHistory.map((attempt) => (
              <li key={attempt.id} className="rounded-lg border border-border/70 px-3 py-2">
                <div className="font-medium text-foreground">
                  {humanizeEnumLabel(attempt.outcome ?? attempt.status)}
                </div>

                <div className="mt-1 text-muted-foreground">
                  {`${humanizeEnumLabel(attempt.mode)} · ${humanizeEnumLabel(attempt.trigger)} · ${formatDateTime(attempt.detectedAt)}`}
                </div>

                {attempt.failureCode ? (
                  <div className="mt-1 text-destructive">
                    {humanizeEnumLabel(attempt.failureCode)}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <Link
        to="/products/$sku"
        params={{ sku: image.configSku }}
        search={{ view: "visuals", store, kind: visualKind }}
        className="mt-5 inline-flex items-center gap-1.5 rounded-full text-[13px] font-medium text-info outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      >
        Open product Visuals
        <ArrowUpRight className="size-3.5" aria-hidden="true" />
      </Link>

      <details className="mt-5 border-t border-border pt-4 text-xs">
        <summary className="cursor-pointer rounded-md font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
          Technical details
        </summary>

        <div className="mt-3">
          <div className="text-muted-foreground">Render fingerprint</div>

          <code className="mt-1 block break-all text-[11px] leading-relaxed text-foreground">
            {image.cell.currentFingerprint ?? "Unavailable"}
          </code>
        </div>

        {image.cell.diff?.state === "unavailable" ? (
          <p className="mt-3 text-muted-foreground">
            Input diff unavailable for this legacy artifact.
          </p>
        ) : null}

        {image.cell.diff?.state === "available" ? (
          <ul className="mt-3 space-y-1 font-mono text-[11px] text-muted-foreground">
            {image.cell.diff.changes.map((change) => (
              <li key={change.path}>{`${change.path}: ${change.before} → ${change.after}`}</li>
            ))}
          </ul>
        ) : null}
      </details>
    </aside>
  )
}

function MetadataRow({
  label,
  value,
  isCode = false,
}: {
  label: string
  value: string
  isCode?: boolean
}) {
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 border-b border-border/70 pb-3 last:border-0 last:pb-0">
      <dt className="text-muted-foreground">{label}</dt>

      <dd className={isCode ? "break-all font-mono text-xs text-foreground" : "text-foreground"}>
        {value}
      </dd>
    </div>
  )
}

function provenanceLabel(value: string | null): string {
  return value === null ? "Unknown" : humanizeEnumLabel(value)
}

function aiUsedLabel(aiUsed: boolean | null): string {
  if (aiUsed === null) {
    return "Unknown"
  }
  return aiUsed ? "Yes" : "No"
}
